/* eslint-disable react/jsx-wrap-multilines */
import React from 'react';
import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import TablePaginationUnstyled from '@mui/base/TablePaginationUnstyled';
import { makeStyles } from '@mui/styles';
import { styled } from '@mui/system';
import { blue, grey } from '@mui/material/colors';

const useStyles = makeStyles((theme) => ({
  rowsPagination: {
    '& .MuiTablePagination-toolbar': {
      display: 'flex',
      alignItems: 'baseline',
      margin: `0 ${theme.spacing(2)}`,
    },
    '& select.MuiTablePagination-select': {
      order: -1,
      margin: `${theme.spacing(1)}`,
      marginLeft: 0,
      borderRadius: `${theme.spacing(1)}`,
      padding: `${theme.spacing(1)}`,
      color: `${theme.palette.text.primary}`,
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: `${
          theme.palette.mode === 'dark' ? grey[800] : grey[50]
        }`,
      },
      '&:focus': {
        outline: `1px solid ${
          theme.palette.mode === 'dark' ? blue[400] : blue[200]
        }`,
      },
    },
    '& p.MuiTablePagination-displayedRows': {
      display: 'none',
    },
    '& div.MuiTablePagination-actions': {
      display: 'none',
    },
  },
  pagination: {
    // display: 'flex',
    width: '100%',
    '& .MuiTablePagination-toolbar': {
      display: 'flex',
      alignItems: 'baseline',
      padding: `0 ${theme.spacing(2)}`,
      justifyContent: 'space-between',
      // width: '50%',
    },
    '& .MuiTablePagination-spacer': {
      display: 'none',
    },
    '& p.MuiTablePagination-selectLabel': {
      display: 'none',
    },
    '& select.MuiTablePagination-select': {
      display: 'none',
    },
    '& .MuiTablePagination-actions button': {
      '&:disabled': {
        color: theme.palette.text.disabled,
      },
      '&:hover': {
        background: theme.palette.action.hover,
      },
      color: theme.palette.text.primary,
      cursor: 'pointer',
      padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
      borderRadius: `${theme.spacing(2)}`,
      transition:
        'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    },
  },
}));

const CustomTablePagination = styled(TablePaginationUnstyled)`
  & .MuiTablePagination-actions {
    display: flex;
    gap: 0.25rem;
    & button {
      background: transparent;
      border: none;
      font-family: 'Nanum Gothic';
      font-weight: bold;
      & > span {
        display: none;
      }
      &:first-of-type::after {
        display: block;
        content: '<';
        width: 10px;
      }
      &:last-of-type::after {
        display: block;
        content: '>';
        width: 10px;
      }
    }
  }
`;

const NewTable = (props) => {
  const classes = useStyles();
  const {
    orderBy,
    order,
    selectable,
    headerItems,
    contentItems,
    lengthOptions,
    rowsPerPage,
    total,
    page,
    selected,
    handleChangePage,
    handleChangeRowsPerPage,
    handleAllClick,
    handleCheckClick,
    handleSortClick,
  } = props;

  const isSelected = (name) => {
    return selected.indexOf(name) !== -1;
  };

  return (
    <Paper sx={{ width: '100%', mb: 2 }} elevation={0} variant="outlined">
      <TablePaginationUnstyled
        count={total}
        onPageChange={handleChangePage}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={lengthOptions}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="개씩 보기"
        component="div"
        className={classes.rowsPagination}
      />
      <TableContainer>
        <Table
          sx={{ minWidth: 750 }}
          aria-labelledby="tableTitle"
          size="medium"
        >
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    color="secondary"
                    indeterminate={
                      selected.length > 0 &&
                      selected.length < contentItems.length
                    }
                    checked={
                      contentItems.length > 0 &&
                      selected.length === contentItems.length
                    }
                    onChange={handleAllClick}
                    inputProps={{
                      'aria-label': 'select all',
                    }}
                  />
                </TableCell>
              )}
              {headerItems.map(({ content, name }) => {
                return (
                  <TableCell
                    align="left"
                    padding="normal"
                    key={name}
                    sortDirection={orderBy === name ? order : false}
                  >
                    {name === 'index' && content}
                    {name !== 'index' && (
                      <TableSortLabel
                        active={orderBy === name}
                        direction={orderBy === name ? order : 'asc'}
                        onClick={handleSortClick && handleSortClick(name)}
                      >
                        {content}
                      </TableSortLabel>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {contentItems.map(({ key, item, id, disabled }) => (
              <TableRow key={key} onClick={(e) => handleCheckClick(e, id)}>
                {selectable && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="secondary"
                      checked={isSelected(id)}
                      disabled={disabled || false}
                    />
                  </TableCell>
                )}
                {item.map(({ content, key: k }) => (
                  <TableCell key={`${key}_${k}`}>{content}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <CustomTablePagination
                colSpan={
                  selectable ? headerItems.length + 1 : headerItems.length
                }
                count={total}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} 개 / 총 ${count}개`
                }
                className={classes.pagination}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default NewTable;
